import React from 'react'
import styled from 'styled-components'
import { media } from 'src/styles/styled-components'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import ArBaseImage from 'src/components/ArBaseImage/ArBaseImage'
import ArBaseContainer from 'src/components/ArBaseContainer/ArBaseContainer'
import ArSwiper from 'src/components/ArSwiper/ArSwiper'
import { generateJSX } from 'src/utils'
import deviceInfoProvider from 'src/utils/deviceInfoProvider'
import ArUnderlinedLink from 'src/components/typography/ArUnderlinedLink'

const StyledWrapper = styled.section`
  padding: 50px 0 30px;
  ${ media.tablet`
    padding: 70px 0 100px;
  ` }
  ${ media.desktop`
    padding: 100px 0 180px;
  ` }
`

const StyledHeader = styled.header`
  margin: 0 auto 60px;

  ${ media.tablet`
    max-width: 430px;
    margin-bottom: 50px;
  ` }

  ${ media.desktop`
    margin-bottom: 100px;
    max-width: none;
  ` }
`

const StyledHeaderTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;

  ${ media.tablet`
    margin-bottom: 17px;
    font-size: 3.8rem;
  ` }

  ${ media.desktop`
    margin-bottom: 10px;
    font-size: 4.5rem;
  ` }
`

const StyledHeaderDescription = styled.p`
  font-size: 2.2rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  letter-spacing: 0.01em;
  line-height: 1.55;
  text-align: center;

  ${ media.tablet`
    font-size: 1.8rem;
  ` }

  ${ media.desktop`
    font-size: 2.2rem;
  ` }
`

const StyledColumns = styled.div`
  > div {
    ${ media.tablet`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        max-width: 82.6%;
        margin: 0 auto;
        margin-bottom: -65px;
    ` }

    ${ media.desktop`
      margin-bottom: -105px;
    ` }
  }
`

const StyledItem = styled.div`
  margin-bottom: 20px;

  a {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  ${ media.tablet`
    width: calc(50% - 26px);
    margin-bottom: 65px;
  ` }

  ${ media.desktop`
    margin-bottom: 105px;
  ` }
`

const StyledLink = styled(Link)`

`

const StyledItemImage = styled(ArBaseImage)`
  max-width: 240px;
  margin: 0 auto 30px;
  height: 150px;

  ${ media.tablet`
    height: auto;
    max-width: 185px;
    margin-bottom: 25px;
  ` }

  ${ media.desktop`
    margin-bottom: 20px;
    max-width: 100px;
  ` }
`

const StyledItemTitle = styled.h3`
  font-size: 2.2rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Normal };
  line-height: 1.2;
  margin-bottom: 10px;
  text-align: center;

  ${ media.tablet`
    font-size: 2.6rem;
    margin-bottom: 13px;
  ` }
`

const StyledItemDescription = styled.p`
  font-size: 1.8rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  letter-spacing: 0.01em;
  line-height: 1.55;
  margin: 0 auto;
  max-width: 380px;
  text-align: center;
`

const StyledCTA = styled.p`
  display: none;

  ${ media.tablet`
    display: block;
    font-family: ${ p => p.theme.font.secondary };
    font-weight: ${ p => p.theme.fontWeight.secondary.Regular };
    font-size: 1.4rem;
    line-height: 2;
    letter-spacing: 0.01em;
    margin-top: 14px;
    color: ${ p => p.theme.colors.purple };
    ${ ArUnderlinedLink }
  ` }
`

class ArColumnsWithDescription extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    description: PropTypes.oneOfType([ PropTypes.string, PropTypes.array ]),
    columns: PropTypes.array,
  }

  render () {
    const { title, description, columns, device } = this.props
    const renderSwiper = device.isMobile
    const Component = renderSwiper ? ArSwiper : 'div'
    const componentProps = renderSwiper ? {
      inversePaginationColor: true,
    } : {}

    return (
      <StyledWrapper>
        <ArBaseContainer>
          <StyledHeader>
            <StyledHeaderTitle>{ title }</StyledHeaderTitle>
            <StyledHeaderDescription>{ generateJSX(description) }</StyledHeaderDescription>
          </StyledHeader>

          <StyledColumns>
            <Component {...componentProps}>
              {columns.map(({ image, title, description, cta }, index) =>
                <StyledItem key={index}>
                  <StyledLink to={cta}>
                    <StyledItemImage src={image.url} alt={image.alt}></StyledItemImage>
                    <StyledItemTitle>{ title }</StyledItemTitle>
                    <StyledItemDescription>{ description }</StyledItemDescription>
                    <StyledCTA>Learn More</StyledCTA>
                  </StyledLink>
                </StyledItem>
              )}
            </Component>
          </StyledColumns>
        </ArBaseContainer>
      </StyledWrapper>

    )
  }
}

export default deviceInfoProvider(ArColumnsWithDescription)
